import { useState, useEffect } from "react";
import api from "@/lib/api";

export default function MonthlyMovementContent() {
  const [tab, setTab] = useState("meals");
  const [days, setDays] = useState(30);
  const [mealMov, setMealMov] = useState(null);
  const [dryMov, setDryMov] = useState(null);

  useEffect(() => {
    if (tab === "meals") {
      api.get(`/meals/movement?days=${days}`).then((r) => setMealMov(r.data));
    } else {
      // Aggregate dry-goods movement from /inventory/transactions
      api.get(`/inventory/transactions?limit=1000`).then((r) => {
        const cutoff = Date.now() - days * 86400000;
        const items = r.data.filter((t) => new Date(t.timestamp).getTime() >= cutoff);
        let added = 0, taken = 0;
        const byVariant = {};
        items.forEach((t) => {
          const d = Number(t.delta || 0);
          if (t.kind === "physical_count") return; // counts don't count as movement
          if (d >= 0) added += d; else taken += -d;
          const vid = t.variant_id || "?";
          byVariant[vid] = (byVariant[vid] || 0) + Math.abs(d);
        });
        const top = Object.entries(byVariant).sort((a, b) => b[1] - a[1]).slice(0, 10);
        setDryMov({ added, taken, net: added - taken, count: items.length, top });
      }).catch(() => setDryMov({ added: 0, taken: 0, net: 0, count: 0, top: [] }));
    }
  }, [tab, days]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button onClick={() => setTab("meals")} className={`h-8 px-3 border rounded-sm text-[10px] font-bold uppercase tracking-widest ${tab === "meals" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Meal Movement</button>
        <button onClick={() => setTab("dry")} className={`h-8 px-3 border rounded-sm text-[10px] font-bold uppercase tracking-widest ${tab === "dry" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Dry-Goods Movement</button>
        <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="ml-auto h-8 px-2 border border-border bg-background rounded-sm text-xs">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {tab === "meals" && mealMov && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div className="border border-border p-2 rounded-sm">
              <div className="label-tech">Added</div>
              <div className="font-display font-black text-xl text-success">{mealMov.added.toFixed(0)}</div>
            </div>
            <div className="border border-border p-2 rounded-sm">
              <div className="label-tech">Take Out</div>
              <div className="font-display font-black text-xl text-blue-500">{mealMov.takeout.toFixed(0)}</div>
            </div>
            <div className="border border-border p-2 rounded-sm">
              <div className="label-tech">Packs</div>
              <div className="font-display font-black text-xl text-purple-500">{mealMov.packs.toFixed(0)}</div>
            </div>
            <div className="border border-border p-2 rounded-sm">
              <div className="label-tech">Net</div>
              <div className={`font-display font-black text-xl ${mealMov.net >= 0 ? "text-success" : "text-destructive"}`}>{mealMov.net.toFixed(0)}</div>
            </div>
          </div>
          <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/60 border-b border-border">
                <tr className="text-left label-tech">
                  <th className="px-2 py-1.5">Diet Type</th>
                  <th className="px-2 py-1.5 text-right">Added</th>
                  <th className="px-2 py-1.5 text-right">Take Out</th>
                  <th className="px-2 py-1.5 text-right">Packs</th>
                  <th className="px-2 py-1.5 text-right">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {mealMov.by_diet_type.map((d) => (
                  <tr key={d.diet_type}>
                    <td className="px-2 py-1">{d.diet_type}</td>
                    <td className="px-2 py-1 text-right font-mono text-success">{d.added.toFixed(0)}</td>
                    <td className="px-2 py-1 text-right font-mono text-blue-500">{d.takeout.toFixed(0)}</td>
                    <td className="px-2 py-1 text-right font-mono text-purple-500">{d.packs.toFixed(0)}</td>
                    <td className="px-2 py-1 text-right font-mono">{(d.added - d.takeout - d.packs).toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Simple bar chart */}
          <div className="mt-3">
            <div className="label-tech mb-1">Additions by Diet Type</div>
            <div className="space-y-1">
              {mealMov.by_diet_type.map((d) => {
                const max = Math.max(...mealMov.by_diet_type.map((x) => x.added), 1);
                const pct = (d.added / max) * 100;
                return (
                  <div key={d.diet_type} className="flex items-center gap-2 text-xs">
                    <span className="w-20 truncate">{d.diet_type}</span>
                    <div className="flex-1 h-3 bg-muted rounded-sm overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-10 text-right font-mono">{d.added.toFixed(0)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
      {tab === "dry" && dryMov && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div className="border border-border p-2 rounded-sm">
              <div className="label-tech">Added</div>
              <div className="font-display font-black text-xl text-success">{dryMov.added.toFixed(0)}</div>
            </div>
            <div className="border border-border p-2 rounded-sm">
              <div className="label-tech">Taken Out</div>
              <div className="font-display font-black text-xl text-blue-500">{dryMov.taken.toFixed(0)}</div>
            </div>
            <div className="border border-border p-2 rounded-sm">
              <div className="label-tech">Net</div>
              <div className={`font-display font-black text-xl ${dryMov.net >= 0 ? "text-success" : "text-destructive"}`}>{dryMov.net.toFixed(0)}</div>
            </div>
            <div className="border border-border p-2 rounded-sm">
              <div className="label-tech">Transactions</div>
              <div className="font-display font-black text-xl">{dryMov.count}</div>
            </div>
          </div>
          <div className="border border-border rounded-sm overflow-hidden">
            <div className="px-3 py-1.5 label-tech border-b border-border bg-muted/40">Top 10 Fastest-Moving Items</div>
            {dryMov.top.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground text-center">No dry-goods transactions in this window.</div>
            ) : (
              <div className="p-2 space-y-1">
                {dryMov.top.map(([vid, total]) => {
                  const max = dryMov.top[0][1] || 1;
                  const pct = (total / max) * 100;
                  return (
                    <div key={vid} className="flex items-center gap-2 text-xs">
                      <span className="w-32 font-mono truncate">{vid}</span>
                      <div className="flex-1 h-3 bg-muted rounded-sm overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-12 text-right font-mono">{total.toFixed(0)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
